import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import OpenConstructionCostInfo from '@/components/Approval/@components/OpenConstructionCost';
import OpenConstructionCostSettlementInfo from '@/components/Approval/@components/OpenConstructionCostSettlement';
import {
  APPROVAL_SECTION_GAP,
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import { AiFileUploader } from '@/components/Common/FileUpload';
import {
  AI_REPORT_ALLOWED_EXTENSIONS,
  APPROVAL_INTERIOR_COST_ADEQUACY,
  APPROVAL_INTERIOR_SETTLEMENT,
  getApprovalAiDraftSourceUrl,
} from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import type { Attachment, AttachmentUploadResult } from '@/types/approval';
import { fetchApi } from '@/utils/api/customAxios';
import {
  getReportFile,
  isOpenConstructionCostSettlementType,
} from '@/utils/approval/openConstructionCost';
import type { ApprovalFormValues } from '../types';
import useAiUploadAttachments from '../useAiUploadAttachments';

const buildFiles = (uploaded: AttachmentUploadResult, file: File) => [
  { fileUuid: uploaded.uuid, mimeType: file.type || 'text/html' },
];

/**
 * ai-draft-source 결과 — 진행 중(in_progress)엔 display.results 가 [null] 로 와서 status 로 먼저 거름.
 * 끝났는데 결과가 비었거나 실패 상태면 에러
 */
const parseResult = (
  data: unknown,
): { result: unknown[] } | { error?: string } | null => {
  const d = data as {
    status?: string | null;
    display?: { results?: unknown[] };
  } | null;
  if (!d || d.status === 'in_progress') return null;
  const results = (d.display?.results ?? []).filter(
    (r) => r && typeof r === 'object',
  );
  if (results.length) return { result: results };
  if (d.status && /fail|error/i.test(d.status)) return { error: '' };
  return null;
};

type OpenConstructionCostFieldProps = {
  /** 233 매장 OPEN · 234 인테리어 OPEN 비용 정산 */
  apvType: number;
  /** 인테리어 보고서에서 상신 진입 — 보고서 결과로 채우고 업로더는 숨김(레거시 loadAiResult) */
  entryAnalysisId?: string;
};

/**
 * 매장 OPEN(233)·인테리어 OPEN 비용 정산(234) 본문 위 폼 — 레거시 appr_edit_forms_open_construction_cost(_settlement).
 * 인테리어 보고서 진입(analysisId)이 기본, 「리포트 등록」 AI 업로더(html)는 보조 경로. 리포트 화면만 양식별
 */
const OpenConstructionCostField = ({
  apvType,
  entryAnalysisId,
}: OpenConstructionCostFieldProps) => {
  const aiDraftSourceUrl = getApprovalAiDraftSourceUrl(apvType);
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { watch, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = watch('openConstructionCost');
  // 보고서를 못 불러오면 업로드로 이어갈 수 있게 업로더를 다시 보임
  const [entryFailed, setEntryFailed] = useState(false);
  const loadedEntryRef = useRef<string | null>(null);

  useEffect(() => {
    if (!entryAnalysisId || loadedEntryRef.current === entryAnalysisId) return;
    loadedEntryRef.current = entryAnalysisId;
    setEntryFailed(false);
    fetchApi<{ data?: unknown }>({
      method: 'GET',
      url: `${
        isOpenConstructionCostSettlementType(apvType)
          ? APPROVAL_INTERIOR_SETTLEMENT
          : APPROVAL_INTERIOR_COST_ADEQUACY
      }/${entryAnalysisId}`,
    })
      .then((res) => {
        const record = res?.data;
        if (!record || typeof record !== 'object') throw new Error('empty');
        const data = [record];
        // 보고서 id 를 그대로 저장 — 레거시 VC001(analysisId) 와 같은 값이라 보고서 ↔ 품의 연결이 유지됨
        setValue('openConstructionCost', {
          aiPipelineId: entryAnalysisId,
          data,
        });
        // 원본 HTML 리포트 자동 첨부(레거시 attachReportFile) — 이미 있으면 생략
        const rf = getReportFile(data);
        const attachments = getValues('attachments');
        if (
          rf &&
          !attachments.some(
            (a) => a.fileUuid === rf.fileUuid || a.fileName === rf.fileName,
          )
        ) {
          const ext = rf.fileName.split('.').pop()?.toLowerCase() ?? '';
          const next: Attachment = {
            fileUuid: rf.fileUuid,
            fileName: rf.fileName,
            fileSize: 0,
            fileFormat: ext,
            uploadDate: '',
            urlOrg: '',
          };
          setValue('attachments', [...attachments, next]);
        }
      })
      .catch(() => {
        setEntryFailed(true);
        snackbar.showError({
          message: t(
            '보고서 결과를 불러오지 못했습니다. 리포트 파일을 등록해 주세요.',
          ),
        });
      });
  }, [apvType, entryAnalysisId, getValues, setValue, snackbar, t]);

  const showUploader = !entryAnalysisId || entryFailed;
  const { onUploadStart, onUploadSettled, onUploaded } =
    useAiUploadAttachments();

  const handleResult = useCallback(
    (result: unknown[], aiPipelineId: string) =>
      setValue(
        'openConstructionCost',
        { aiPipelineId, data: result },
        { shouldDirty: true },
      ),
    [setValue],
  );

  return (
    <Box
      className="open-construction-cost-form"
      sx={{ mb: `${APPROVAL_SECTION_GAP}px` }}
    >
      <Box
        sx={{
          mb: `${APPROVAL_SECTION_GAP_LG}px`,
          display: showUploader ? undefined : 'none',
        }}
      >
        <InfoSectionTitle sx={{ mb: 1 }}>{t('리포트 등록')}</InfoSectionTitle>
        <AiFileUploader<unknown[]>
          requestUrl={aiDraftSourceUrl}
          getResultUrl={(id) => `${aiDraftSourceUrl}/${id}`}
          buildFiles={buildFiles}
          documentType={apvType}
          uploadVariant="ai-report"
          allowedExtensions={AI_REPORT_ALLOWED_EXTENSIONS}
          parseResult={parseResult}
          onUploadStart={onUploadStart}
          onUploadSettled={onUploadSettled}
          onUploaded={onUploaded}
          onResult={handleResult}
        />
      </Box>
      {isOpenConstructionCostSettlementType(apvType) ? (
        <OpenConstructionCostSettlementInfo data={state?.data ?? []} />
      ) : (
        <OpenConstructionCostInfo data={state?.data ?? []} />
      )}
    </Box>
  );
};

export default OpenConstructionCostField;
